import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Tag, Eye, AlertCircle, ArrowUp, ArrowDown, User } from 'lucide-react';

const GestionCategories = () => {
  // ============================================================================
  // STATES
  // ============================================================================
  const [categories, setCategories] = useState([]);
  const [ouvriers, setOuvriers] = useState([]); // 👈 NOUVEAU
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCategorie, setSelectedCategorie] = useState(null);
  
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    icone: '🏷️',
    couleur: '#10b981',
    ordre: 0,
    ouvrier_principal_id: null, // 👈 NOUVEAU
    actif: 'oui'
  });

  const ICONES_DISPONIBLES = [
    '🥕', '🥬', '🍅', '🧅', '🍎', '🥚', '🧀', '🥩', '🍯', '📦',
    '🥔', '🌽', '🥒', '🫑', '🍓', '🍊', '🥛', '🗡', '🥖', '🌶️',
    '🥦', '🍆', '🥑', '🫐', '🍊', '🥓', '🍞', '🧈', '🫒', '🌰'
  ];

  const COULEURS_DISPONIBLES = [
    { nom: 'Vert', code: '#10b981' },
    { nom: 'Orange', code: '#f59e0b' },
    { nom: 'Rouge', code: '#ef4444' },
    { nom: 'Violet', code: '#8b5cf6' },
    { nom: 'Rose', code: '#ec4899' },
    { nom: 'Jaune', code: '#eab308' },
    { nom: 'Cyan', code: '#06b6d4' },
    { nom: 'Bleu', code: '#3b82f6' },
    { nom: 'Indigo', code: '#6366f1' },
    { nom: 'Gris', code: '#6b7280' }
  ];

  // ============================================================================
  // TOAST
  // ============================================================================
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const Toast = () => {
    if (!toast.show) return null;
    const bgColor = toast.type === 'success' ? 'bg-green-500' : 
                    toast.type === 'error' ? 'bg-red-500' : 'bg-blue-500';
    return (
      <div className={`fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2`}>
        {toast.type === 'success' && '✅'}
        {toast.type === 'error' && '❌'}
        {toast.type === 'info' && 'ℹ️'}
        <span>{toast.message}</span>
        <button onClick={() => setToast({ ...toast, show: false })} className="ml-2">×</button>
      </div>
    );
  };

  // ============================================================================
  // CHARGEMENT DONNÉES
  // ============================================================================
  const loadCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      if (data.success) setCategories(data.data);
    } catch (error) {
      showToast('Erreur chargement catégories', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 👇 NOUVEAU : Charger les ouvriers
  const loadOuvriers = async () => {
    try {
      const response = await fetch('/api/personnel');
      const data = await response.json();
      if (data.success) {
        setOuvriers(data.data.filter(o => o.actif === 1));
      }
    } catch (error) {
      console.error('Erreur chargement ouvriers:', error);
    }
  };

  useEffect(() => {
    loadCategories();
    loadOuvriers(); // 👈 NOUVEAU
  }, []);

  // ============================================================================
  // GESTION FORMULAIRE
  // ============================================================================
  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      icone: '🏷️',
      couleur: '#10b981',
      ordre: categories.length + 1,
      ouvrier_principal_id: null, // 👈 NOUVEAU
      actif: 'oui'
    });
    setSelectedCategorie(null);
  };

  const handleCreate = () => {
    resetForm();
    setModalMode('create');
    setShowModal(true);
  };

  const handleEdit = (categorie) => {
    setFormData({
      nom: categorie.nom || '',
      description: categorie.description || '',
      icone: categorie.icone || '🏷️',
      couleur: categorie.couleur || '#10b981',
      ordre: categorie.ordre || 0,
      ouvrier_principal_id: categorie.ouvrier_principal_id || null, // 👈 NOUVEAU
      actif: categorie.actif || 'oui'
    });
    setSelectedCategorie(categorie);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.nom.trim()) {
      showToast('Le nom de la catégorie est obligatoire', 'error');
      return;
    }

    const url = modalMode === 'create' 
      ? '/api/categories'
      : `/api/categories/${selectedCategorie.id}`;
    
    const method = modalMode === 'create' ? 'POST' : 'PUT';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        await loadCategories();
        setShowModal(false);
        resetForm();
        showToast(`Catégorie ${modalMode === 'create' ? 'créée' : 'modifiée'} avec succès !`, 'success');
      } else {
        showToast(`Erreur: ${data.message}`, 'error');
      }
    } catch (error) {
      showToast(`Erreur réseau: ${error.message}`, 'error');
    }
  };

  const handleDelete = async (categorie) => {
    if (categorie.nb_produits > 0) {
      showToast(`Impossible de supprimer : ${categorie.nb_produits} produit(s) liés`, 'error');
      return;
    }
    if (!confirm(`Supprimer la catégorie "${categorie.nom}" ?`)) return;

    try {
      const response = await fetch(`/api/categories/${categorie.id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        await loadCategories();
        showToast('Catégorie supprimée avec succès', 'success');
      } else {
        showToast(`Erreur: ${data.message}`, 'error');
      }
    } catch (error) {
      showToast(`Erreur réseau: ${error.message}`, 'error');
    }
  };

  const handleChangeOrdre = async (categorie, direction) => {
    const newOrdre = direction === 'up' ? categorie.ordre - 1 : categorie.ordre + 1;
    try {
      const response = await fetch(`/api/categories/${categorie.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...categorie, ordre: newOrdre })
      });
      const data = await response.json();
      if (data.success) await loadCategories();
    } catch (error) {
      showToast('Erreur changement ordre', 'error');
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  if (loading) {
    return <div className="flex justify-center items-center h-64">
      <div className="text-lg">Chargement des catégories...</div>
    </div>;
  }

  return (
    <div className="space-y-6">
      <Toast />

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-green-800">🏷️ Gestion des Catégories</h2>
          <p className="text-sm text-gray-600 mt-1">
            Organisez vos produits en catégories avec icônes, couleurs et ouvrier récoltant
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
        >
          <Plus className="w-5 h-5" />
          Nouvelle Catégorie
        </button>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-100 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-800">{categories.length}</div>
          <div className="text-sm text-blue-600">Catégories total</div>
        </div>
        <div className="bg-green-100 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-800">
            {categories.filter(c => c.actif === 'oui').length}
          </div>
          <div className="text-sm text-green-600">Catégories actives</div>
        </div>
        <div className="bg-purple-100 p-4 rounded-lg">
          <div className="text-2xl font-bold text-purple-800">
            {categories.reduce((sum, c) => sum + parseInt(c.nb_produits || 0), 0)}
          </div>
          <div className="text-sm text-purple-600">Produits classés</div>
        </div>
        <div className="bg-orange-100 p-4 rounded-lg">
          <div className="text-2xl font-bold text-orange-800">
            {categories.filter(c => c.ouvrier_principal_id).length}
          </div>
          <div className="text-sm text-orange-600">Avec ouvrier assigné</div>
        </div>
      </div>

      {/* TABLEAU */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ordre</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">👨‍🌾 Ouvrier</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Couleur</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produits</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categories.map((categorie) => (
              <tr key={categorie.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-gray-700">{categorie.ordre}</span>
                    <div className="flex flex-col">
                      <button
                        onClick={() => handleChangeOrdre(categorie, 'up')}
                        className="text-gray-400 hover:text-green-600"
                        disabled={categorie.ordre === 1}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleChangeOrdre(categorie, 'down')}
                        className="text-gray-400 hover:text-green-600"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{categorie.icone}</span>
                    <div>
                      <div className="font-medium text-gray-900">{categorie.nom}</div>
                      <div className="text-xs text-gray-500">ID: {categorie.id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-gray-600 max-w-xs truncate">
                    {categorie.description || '-'}
                  </div>
                </td>
                
                {/* 👇 COLONNE OUVRIER (NOUVEAU) */}
                <td className="px-4 py-4">
                  {categorie.ouvrier_nom ? (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-green-600" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{categorie.ouvrier_nom}</div>
                        {categorie.ouvrier_tel && (
                          <div className="text-xs text-gray-500">{categorie.ouvrier_tel}</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">Non assigné</span>
                  )}
                </td>

                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded border-2 border-gray-200"
                      style={{ backgroundColor: categorie.couleur }}
                    ></div>
                    <span className="text-xs text-gray-500">{categorie.couleur}</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {categorie.nb_produits || 0} produit(s)
                  </span>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    categorie.actif === 'oui' 
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {categorie.actif === 'oui' ? (
                      <><Eye className="w-3 h-3 mr-1" />Actif</>
                    ) : 'Inactif'}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(categorie)}
                      className="text-yellow-600 hover:text-yellow-800"
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(categorie)}
                      className="text-red-600 hover:text-red-800"
                      title="Supprimer"
                      disabled={categorie.nb_produits > 0}
                    >
                      <Trash2 className={`w-4 h-4 ${categorie.nb_produits > 0 ? 'opacity-30 cursor-not-allowed' : ''}`} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODAL FORMULAIRE */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold">
                {modalMode === 'create' ? '➕ Nouvelle Catégorie' : '✏️ Modifier Catégorie'}
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la catégorie *
                </label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Ex: Légumes racines"
                  required
                />
              </div>

              {/* 👇 COMBO OUVRIER (NOUVEAU) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  👨‍🌾 Ouvrier récoltant principal
                </label>
                <select
                  value={formData.ouvrier_principal_id || ''}
                  onChange={(e) => setFormData({
                    ...formData, 
                    ouvrier_principal_id: e.target.value ? parseInt(e.target.value) : null
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">-- Aucun ouvrier assigné --</option>
                  {ouvriers.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.nom} {o.specialites ? `(${o.specialites})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Cet ouvrier sera automatiquement assigné aux produits de cette catégorie lors de l'impression des fiches de récolte
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={2}
                  placeholder="Ex: Pommes de terre, carottes, navets..."
                />
              </div>

              {/* Icône */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icône / Emoji
                </label>
                <div className="grid grid-cols-10 gap-2 mb-3">
                  {ICONES_DISPONIBLES.map((icone, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setFormData({...formData, icone})}
                      className={`text-2xl p-2 rounded border-2 hover:border-green-500 ${
                        formData.icone === icone ? 'border-green-500 bg-green-50' : 'border-gray-200'
                      }`}
                    >
                      {icone}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.icone}
                  onChange={(e) => setFormData({...formData, icone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-2xl text-center"
                  placeholder="Ou tapez votre propre emoji"
                  maxLength={2}
                />
              </div>

              {/* Couleur */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur
                </label>
                <div className="grid grid-cols-5 gap-3 mb-3">
                  {COULEURS_DISPONIBLES.map((couleur, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setFormData({...formData, couleur: couleur.code})}
                      className={`h-12 rounded border-2 hover:scale-105 transition-transform ${
                        formData.couleur === couleur.code ? 'border-gray-800 ring-2 ring-gray-400' : 'border-gray-200'
                      }`}
                      style={{ backgroundColor: couleur.code }}
                      title={couleur.nom}
                    ></button>
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.couleur}
                  onChange={(e) => setFormData({...formData, couleur: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="#10b981"
                />
              </div>

              {/* Aperçu */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">Aperçu :</p>
                <div 
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium"
                  style={{ 
                    backgroundColor: `${formData.couleur}20`, 
                    color: formData.couleur 
                  }}
                >
                  <span className="text-xl mr-2">{formData.icone}</span>
                  {formData.nom || 'Nom de la catégorie'}
                </div>
              </div>

              {/* Ordre + Statut */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ordre d'affichage
                  </label>
                  <input
                    type="number"
                    value={formData.ordre}
                    onChange={(e) => setFormData({...formData, ordre: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    value={formData.actif}
                    onChange={(e) => setFormData({...formData, actif: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="oui">✅ Actif</option>
                    <option value="non">❌ Inactif</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Boutons action */}
            <div className="flex justify-end gap-3 p-6 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {modalMode === 'create' ? 'Créer' : 'Modifier'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionCategories;